import React, { useContext, useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import notify from '~/components/Notify';
import { El } from '~/components/Layout/style';
import { Input } from 'semantic-ui-react';
import { useDebounce } from 'use-lodash-debounce'
import { RouteTypes } from '~/routes/constants';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import i18n from '~/components/i18n'
import { useAuth } from '~/routes/components/AuthProvider';
import 'dayjs/locale/de';
import 'dayjs/locale/fr';
import { StyledTab } from '~/styles/table';
import ProfileSettingsService from '~/api/services/profileSettings';
import { ThemeContext } from 'styled-components';
import TableComponent from '~/components/TableComponent';
import { DownloadService } from '~/api/services/downloadFiles';
import Dialog from '~/components/Dialog';
import { useHash } from '~/components/useHash';
import { ADMIN_ROLE_SLUG, FARMER_MANAGER_ROLE_SLUG, IFE_ROLE_SLUG, LIGHT_USER_ROLE_SLUG, OPERATIVE_ROLE_SLUG } from '~/Constants';
import TwoFaComp from '~/components/TwoFaComp';
import PlanModal from '~/components/PlanModal';
import { StockExchangeQuery } from '~/QueryHooks/StockExchangeQuery';
import StockExChangeService from '~/api/services/stockExchange';
import { handleErrors } from '~/utils/helpers';

interface IMilkFixedPrice { }

const getActions = () => [
  {
    popupMessage: i18n.t('Show more'),
    type: 'link',
    name: 'eye',
    size: 'large',
    link: RouteTypes.STOCK_EXCHANGE_SHOW,
    lightUserAction: true,
    farmerManagerAction: true
  },
  {
    popupMessage: i18n.t('Edit'),
    type: 'link',
    name: 'pencil',
    size: 'large',
    link: RouteTypes.STOCK_EXCHANGE_EDIT,
    display: ['defined']
  },
  {
    popupMessage: i18n.t('Plan and publish'),
    type: 'icon',
    name: 'share',
    size: 'large',
    actionType: 'plan',
    display: ['defined']
  },
  {
    popupMessage: i18n.t('Results'),
    type: 'link',
    name: 'clipboard outline',
    size: 'large',
    link: RouteTypes.STOCK_EXCHANGE_RESULTS,
    display: ['active', 'completed', 'examination'],
    lightUserAction: true
  },
  {
    popupMessage: i18n.t('Offers preview'),
    type: 'link',
    name: 'handshake outline',
    size: 'large',
    link: RouteTypes.STOCK_EXCHANGE_OFFERS_PREVIEW,
    display: ['active', 'examination'],
    lightUserAction: true
  },
  {
    popupMessage: i18n.t('Offers preview (PDF)'),
    type: 'icon',
    name: 'file pdf outline',
    size: 'large',
    actionType: 'offerPreview',
    display: ['completed', 'examination'],
    lightUserAction: true,
    farmerManagerAction: true
  },
  {
    popupMessage: i18n.t('Summary (PDF)'),
    type: 'icon',
    actionType: 'summary',
    name: 'file pdf outline',
    size: 'large',
    display: ['completed'],
    lightUserAction: true,
    farmerManagerAction: true
  },
  {
    popupMessage: i18n.t('Abort trading slot'),
    type: 'icon',
    name: 'close',
    size: 'large',
    actionType: 'abort',
    display: ['defined', 'scheduled', 'active'],
    color: 'red'
  }
]

const StockExchange: React.FunctionComponent<IMilkFixedPrice> = () => {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false)
  const { systemData, systemData: { amountUnit, logged: { roleSlug } } } = useAuth()
  const [hash, setHash] = useHash();
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 30,
    search: '',
    status: hash !== '' ? hash.toString().split('#')[1] : 'actual'
  })
  const actions = getActions()
  const [search, setSearch] = useState('')
  const [abortTradingSlotId, setAbortTradingSlotId] = useState(null)
  const debouncedValue = useDebounce(search, 300)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const tfaAbortTradingSlot = systemData?.roleSettings?.tfaAbortTradingSlot === 1
  const tfaType = systemData?.logged?.tfaType
  const [correctTfaCode, setCorrectTfaCode] = useState(false)
  const [tfaCode, setTfaCode] = useState('')
  const [planingRow, setPlaningRow] = useState(null)
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const { firstColor } = useContext(ThemeContext)

  const defaultTableColumnHeaders = [
    {
      name: t('Status'),
      key: 'statusLabel'
    },
    {
      name: t('Trading volume type'),
      key: 'tradingVolumeTypeName'
    },
    {
      name: t('Trading slot ID'),
      key: 'uniqueId'
    },
    {
      name: t('Start/End'),
      key: 'dateFrom',
      type: 'startEnd',
      sorted: 'desc'
    },
    {
      name: t('Number of farmers (in %)'),
      key: 'totalFarmers',
      type: 'totalFarmers'
    },
    {
      name: t('Number of offers'),
      key: 'totalOffers',
      type: 'no-unit-number',
      decimalScale: 0
    },
    {
      name: t('Offered amount (in {{amountUnit}})', { amountUnit }),
      key: 'totalOfferedAmount',
      type: 'number'
    },
    {
      name: t('Hedging amount (in {{amountUnit}})', { amountUnit }),
      key: 'totalConfirmedAmount',
      type: 'number'
    },
    {
      name: t('Max trading amount (in {{amountUnit}})', { amountUnit }),
      key: 'totalAmount',
      type: 'number'
    },

    {
      name: i18n.t('Actions')
    }
  ]
  const [tableColumnHeaders, setTableColumnHeaders] = useState(defaultTableColumnHeaders)

  const {
    isLoading: isBusyData,
    data: milkFixedData,
    isFetching: isFetchingData,
    refetch: fetchFilteredData
  } = StockExchangeQuery.getMilkFixexPriceList({ ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })

  const {
    data: milkFixedOptions,
    refetch: refetchOptions,
    isLoading: isBusyPriceList
  } = StockExchangeQuery.getMilkFixexPriceListOptions()

  const {
    data: milkFixedCount,
    isLoading: isBusyPriceCount,
    refetch: fetchCount
  } = StockExchangeQuery.getMilkFixexPriceCount({ ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })

  useEffect(() => {
    if (hash !== '') {
      const index = milkFixedOptions?.findIndex((option) => option.key === hash.toString().split('#')[1])
      setActiveTabIndex(index !== -1 ? index : 0)
    }
    dayjs.extend(LocalizedFormat)
  }, [])

  useEffect(() => {
    setTableColumnHeaders(defaultTableColumnHeaders)
    fetchFilteredData()
    refetchOptions()
  }, [t])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData()
      fetchCount()
    }
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  function renderActions() {
    return <>
      <El.PageHeaderActionButton
        as={Link}
        style={{ backgroundColor: firstColor }}
        to={RouteTypes.STOCK_EXCHANGE_CREATE}
      >{t('Create Trading slot')}</El.PageHeaderActionButton>
    </>
  }

  async function handleDownloadSummary(tradingSlot) {
    setLoadingAction({
      type: 'summary',
      id: tradingSlot?.id,
      value: true,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFileInAnotherWindow(`stock-exchange/trading-slots/${tradingSlot.id}/summary/pdf`)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  async function handleDownloadExportPdf(tradingSlot) {
    setLoadingAction({
      type: 'offerPreview',
      id: tradingSlot?.id,
      value: true,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFileInAnotherWindow(`stock-exchange/trading-slots/${tradingSlot.id}/offers-overview/pdf`)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  async function handleAbortTradingSlot(id: string, data?) {
    setIsBusy(true)
    try {
      await StockExChangeService.abortTradingSlot(id, data)
      notify(t('Trading slot aborted'), 'success')
      setAbortTradingSlotId(null)
      setTfaCode('')
      setCorrectTfaCode(false)
      fetchFilteredData()
      fetchCount()
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function handleSendCode(id) {
    if (tfaType === 'email' || tfaType === 'sms') {
      setIsBusy(true)
      try {
        await ProfileSettingsService.sendCode()
        notify(t('Code sent'), 'success')
      } catch (error) {
        handleErrors(error)
      }
      setIsBusy(false)
    }
  }

  function renderSearch() {
    return <Input
      icon={{ name: 'search', circular: true, link: true }}
      placeholder={t('Search...')}
      value={search}
      onChange={(e, data) => setSearch(data.value)}
    />
  }

  function handleClickAbortAction(row) {
    const { id } = row
    if (tfaAbortTradingSlot) {
      setAbortTradingSlotId(id)
      handleSendCode(id)
      return
    }
    setAbortTradingSlotId(id)
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'abort':
        return handleClickAbortAction(row)
      case 'summary':
        return handleDownloadSummary(row)
      case 'offerPreview':
        return handleDownloadExportPdf(row)
      case 'plan':
        return setPlaningRow(row)
      default:
        return
    }
  }

  function getPermitedActions() {
    switch (roleSlug) {
      case LIGHT_USER_ROLE_SLUG:
        return actions.filter(action => !!action.lightUserAction)
      case ADMIN_ROLE_SLUG:
        return actions
      case FARMER_MANAGER_ROLE_SLUG:
        return actions.filter(action => !!action.farmerManagerAction)
      case IFE_ROLE_SLUG:
        return [
          {
            popupMessage: i18n.t('Show more'),
            type: 'link',
            name: 'eye',
            size: 'large',
            link: RouteTypes.STOCK_EXCHANGE_SHOW
          }
        ]
      default:
        return actions
    }
  }

  function renderContent() {
    const permitedActions = getPermitedActions()
    return <TableComponent
      actions={permitedActions}
      isFetchingData={isFetchingData}
      animated
      loadingAction={loadingAction}
      tableColumnHeaders={tableColumnHeaders}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={milkFixedData?.rows || []}
      total={milkFixedData?.filtered || 0}
    />
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      const status = milkFixedOptions.find((_, indexOption) => indexOption === activeIndex)
      setActiveTabIndex(activeIndex)
      setDataFiltered({
        ...dataFiltered,
        status: status.key,
        page: 0
      })
    }
  }

  function renderPanes() {
    const panes = milkFixedOptions.map(option => {
      const count = milkFixedCount.find(count => count.status === option.key)
      return {
        menuItem: `${option.label} (${count?.count || 0})`,
        render: () => renderContent()
      }
    })

    return <StyledTab
      menu={{ secondary: true, pointing: true }}
      activeIndex={activeTabIndex}
      panes={panes}
      onTabChange={(_, data) => handleTabChange(data.activeIndex)}
    />
  }

  function renderAbortTradingModal() {
    return <Dialog
      isDialogOpen={Boolean(abortTradingSlotId)}
      textClose={t('No')}
      handleClose={() => {
        setTfaCode('')
        setCorrectTfaCode(false)
        setAbortTradingSlotId(null)
      }}
      disableSubmit={tfaAbortTradingSlot ? !correctTfaCode : false}
      textSubmit={t('Yes')}
      handleSubmit={() => handleAbortTradingSlot(abortTradingSlotId, tfaAbortTradingSlot ? { code: tfaCode } : null)}
      textContent={<>
        <div>{t('Are you sure you want to abort this trading slot?')}</div>
        {tfaAbortTradingSlot && <TwoFaComp
          onChangeTfaCode={(code) => setTfaCode(code)}
          onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
        />}
      </>}
      textHeader={t('Abort trading slot')}
      type={'abort'}
      size={tfaAbortTradingSlot ? 'small' : 'tiny'}
    />
  }

  function isAllowedActionBasedOnRole() {
    switch (roleSlug) {
      case LIGHT_USER_ROLE_SLUG:
        return false
      case ADMIN_ROLE_SLUG:
        return true
      case OPERATIVE_ROLE_SLUG:
        return true
      case FARMER_MANAGER_ROLE_SLUG:
        return false
      case IFE_ROLE_SLUG:
        return false
      default:
        return false
    }
  }

  const loading = isBusy || isBusyPriceCount || isBusyPriceList || isBusyData

  return <Layout
    isBusy={loading}
    segmented
    title={t('Stock exchange')}
    filters={renderSearch()}
    actions={systemData && isAllowedActionBasedOnRole() && renderActions()}
  >
    {milkFixedOptions && milkFixedCount && milkFixedData && renderPanes()}
    {renderAbortTradingModal()}
    {planingRow && <PlanModal
      slotType='stockExchange'
      openDialog={Boolean(planingRow)}
      handleClose={(submited) => {
        if (submited) {
          fetchFilteredData()
          fetchCount()
        }
        setPlaningRow(null)
      }}
      showData={planingRow}
    />}
  </Layout>;
};

export default StockExchange;
